import React from "react";
import {
  IIntegrationObjectCore,
  getAccessChangeTypeString,
  AccessChangeTypes,
  IIntegrationAccessCollection,
} from "../../../../models/IIntegrationAccessCollection";
import { Dialog, Collapse, IconButton, TableBody, TableCell, TableHead, TableRow, DialogTitle, DialogContent, Typography, Grid, Fab } from "@material-ui/core";
import { KeyboardArrowUp, KeyboardArrowDown, Fullscreen, FullscreenExit, Check, Close } from "@material-ui/icons";
import { AddedAccessTableCell, FieldTable, FieldTableRow, ObjectTable, RemovedAccessTableCell } from "../../style/IntegrationStyledComponents";
import FullScreenDialogTitle from "../shared/FullScreenDialogTitle";

interface CustomAPIFieldsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  access: IIntegrationAccessCollection | undefined;
}

const getObjectHeader = () => (
  <TableHead>
    <TableRow>
      <TableCell>Module Name</TableCell>
      <TableCell>Object Name</TableCell>
      <TableCell>Is Delete Allowed</TableCell>
      <TableCell>Delete Expiration Date</TableCell>
      <TableCell>Proposed Delete Access</TableCell>
      <TableCell>Fields</TableCell>
    </TableRow>
  </TableHead>
);

const getFieldHeader = () => (
  <TableHead>
    <TableRow>
      <TableCell>Field Name</TableCell>
      <TableCell>Is Read Allowed</TableCell>
      <TableCell>Read Expiration Date</TableCell>
      <TableCell>Proposed Read Access</TableCell>
      <TableCell>Is Create Allowed</TableCell>
      <TableCell>Create Expiration Date</TableCell>
      <TableCell>Proposed Create Access</TableCell>
      <TableCell>Is Update Allowed</TableCell>
      <TableCell>Update Expiration Date</TableCell>
      <TableCell>Proposed Update Access</TableCell>
    </TableRow>
  </TableHead>
);

const CustomAPIFieldsDialog = (props: CustomAPIFieldsDialogProps) => {
  const { isOpen, onClose, access } = props;

  const [fieldsOpen, setFieldsOpen] = React.useState<Map<IIntegrationObjectCore, boolean>>(new Map<IIntegrationObjectCore, boolean>());

  React.useEffect(() => {
    let initFieldsOpen = new Map<IIntegrationObjectCore, boolean>();
    if (access) {
      access.Objects.forEach(o => initFieldsOpen.set(o, false));
    }
    setFieldsOpen(initFieldsOpen);
  }, [access]);

  const expandAll = () => applyAll(true);

  const collapseAll = () => applyAll(false);

  const applyAll = (isOpen: boolean) => {
    let allFieldsApplied = new Map<IIntegrationObjectCore, boolean>();
    if (access) {
      access.Objects.forEach(o => allFieldsApplied.set(o, isOpen));
    }

    setFieldsOpen(allFieldsApplied);
  };

  const toggleFieldsOpen = (obj: IIntegrationObjectCore) => () => {
    let prevValue: boolean = fieldsOpen.get(obj) || false;

    setFieldsOpen(prev => new Map(prev).set(obj, !prevValue));
  };

  const getBoolIcon = (isTrue: boolean) => (isTrue ? <Check /> : <Close />);
  const getDateText = (date: Date | null) => (date === null ? "None" : date.toDateString());

  const getAccessCell = (type: AccessChangeTypes) => {
    if (access && access.ErrorLoadingProposedAccess) {
      return <RemovedAccessTableCell>Error</RemovedAccessTableCell>;
    }

    switch (type) {
      case AccessChangeTypes.AddAccess:
      case AccessChangeTypes.RemoveExpiration:
        return <AddedAccessTableCell>{getAccessChangeTypeString(type)}</AddedAccessTableCell>;
      case AccessChangeTypes.AddExpiration:
        return <RemovedAccessTableCell>{getAccessChangeTypeString(type)}</RemovedAccessTableCell>;
      case AccessChangeTypes.NoChange:
        return <TableCell>{getAccessChangeTypeString(type)}</TableCell>;
    }
  };

  let areAllExpanded = Array.from(fieldsOpen.values()).reduce((f1, f2) => f1 && f2, true);
  let areAllCollapsed = !Array.from(fieldsOpen.values()).reduce((f1, f2) => f1 || f2, false);

  return (
    <>
      <Dialog open={isOpen} onClose={onClose} area-labelledby="fields-dialog-title" fullScreen>
        <FullScreenDialogTitle title="Custom API Fields" onClose={onClose} />
        <DialogContent>
          <IconButton aria-label="expand all" size="small" onClick={expandAll} disabled={areAllExpanded}>
            <Fullscreen />
            Expand All
          </IconButton>
          <IconButton aria-label="collapse all" size="small" onClick={collapseAll} disabled={areAllCollapsed}>
            <FullscreenExit />
            Collapse All
          </IconButton>
          <ObjectTable>
            {getObjectHeader()}
            <TableBody>
              {access &&
                access.Objects.map((o, oIndex) => {
                  const objectFieldsOpen: boolean | undefined = fieldsOpen.get(o) || false;

                  return (
                    <React.Fragment key={oIndex}>
                      <TableRow style={{ borderBottom: "unset" }}>
                        <TableCell>{o.Module}</TableCell>
                        <TableCell>{o.Object}</TableCell>
                        <TableCell>{getBoolIcon(o.IsDeleteAllowed)}</TableCell>
                        <TableCell>{getDateText(o.DeleteExpirationDate)}</TableCell>
                        {getAccessCell(o.ProposedDeleteAccess)}
                        <TableCell>
                          <IconButton aria-label="expand fields" size="small" onClick={toggleFieldsOpen(o)}>
                            {objectFieldsOpen ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                            Fields
                          </IconButton>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                          <Collapse in={objectFieldsOpen} timeout="auto" unmountOnExit>
                            <FieldTable size="small">
                              {getFieldHeader()}
                              <TableBody>
                                {o.Fields.map((f, fIndex) => (
                                  <FieldTableRow key={`${oIndex}.${fIndex}`}>
                                    <TableCell>{f.Field}</TableCell>
                                    <TableCell>{getBoolIcon(f.IsReadAllowed)}</TableCell>
                                    <TableCell>{getDateText(f.ReadExpirationDate)}</TableCell>
                                    {getAccessCell(f.ProposedReadAccess)}
                                    <TableCell>{getBoolIcon(f.IsCreateAllowed)}</TableCell>
                                    <TableCell>{getDateText(f.CreateExpirationDate)}</TableCell>
                                    {getAccessCell(f.ProposedCreateAccess)}
                                    <TableCell>{getBoolIcon(f.IsUpdateAllowed)}</TableCell>
                                    <TableCell>{getDateText(f.UpdateExpirationDate)}</TableCell>
                                    {getAccessCell(f.ProposedUpdateAccess)}
                                  </FieldTableRow>
                                ))}
                              </TableBody>
                            </FieldTable>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}
            </TableBody>
          </ObjectTable>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CustomAPIFieldsDialog;
